/* eslint-disable react-hooks/exhaustive-deps */
import React, { Fragment, useState } from 'react';
import DataTable from 'components/DataTable/DataTable';
import { Document, Recipients } from 'dataTypes/document';
import { dateToString } from 'utils/formatDate';
import { Box, Avatar, AvatarGroup } from '@mui/material';
import styles from './DocumentTable.module.css';
import MenuButton from 'components/MenuButton/MenuButton';
import { Delete, Edit, FileCopy, Visibility } from '@mui/icons-material';
import { NavLink, useNavigate } from 'react-router-dom';
import EmptyTable from 'components/DataTable/components/EmptyTable/EmptyTable';
import { formatTextUpperCase } from 'utils/formatName';
import { randomAvatarColor, getNameInitials, getContrastColor } from 'utils/documentTableUtils';
import ModalFactory, { ModalType } from 'components/CustomModal/ModalFactory';
import { documentStatus } from 'features/document/constants';
import NewFileButton from 'components/NewFileButton/NewFileButton';

interface DocumentTableProps {
  data: Document[];
}

//TODO: work on table title, customTheme needs to be adjust for typograpny
//TODO: refactor this component
const DocumentTable: React.FC<DocumentTableProps> = ({ data }) => {
  //TODO: think about a menu item component
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<ModalType>('delete');
  const [modalData, setModalData] = useState({});

  const openModal = (data: { id: number; name: string }) => {
    setModalData(data);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };
  const deleteModalProps = {
    onClose: closeModal,
    modalData: modalData,
  };

  const renameModalProps = {
    onClose: closeModal,
    modalData: modalData,
  };

  const modalHandler = () => {
    switch (modalType) {
      case 'delete':
        return <ModalFactory type='delete' props={deleteModalProps} />;

      case 'rename':
        return <ModalFactory type='rename' props={renameModalProps} />;

      case 'duplicate':
        return <ModalFactory type='duplicate' props={renameModalProps} />;

      default:
        return null;
    }
  };

  if (!data.length) {
    return (
      <EmptyTable
        title='There is nothing here!'
        subtitle='Start creating new files to get started'
        buttonComponent={<NewFileButton />}
      />
    );
  }

  const getRecipients = (templateJson: string) => {
    const recipients = JSON.parse(templateJson)?.recipients;
    return (
      <AvatarGroup sx={{ justifyContent: 'flex-end' }} max={5} spacing={-8}>
        {recipients?.map((recipients: Recipients) => (
          <Avatar
            sx={{
              width: 20,
              height: 20,
              bgcolor: randomAvatarColor(recipients?.fullName || ''),
              color: getContrastColor(randomAvatarColor(recipients?.fullName || '')),
              fontSize: '10px',
            }}
            key={recipients?.name}
            alt={getNameInitials(recipients?.fullName)}
            //TODO: Backend doesn't send profile images yet.
          >
            {getNameInitials(recipients?.fullName)}
          </Avatar>
        ))}
      </AvatarGroup>
    );
  };

  const rowsData = data.map(({ name, status, user, createdAt, updatedAt, id, type, templateJson, statusUpdated }) => {
    const getRowOptions = (status: string) => {
      const options = [
        {
          item: (
            <Box display='flex' gap={1} justifyContent='start' alignItems='center'>
              <FileCopy color='primary' />
              Duplicate
            </Box>
          ),
          handler: () => {
            setModalType!('duplicate');
            openModal({ id, name });
          },
        },
        {
          item: (
            <Box display='flex' gap={1} justifyContent='start' alignItems='center'>
              <Delete color='primary' />
              Delete
            </Box>
          ),
          handler: () => {
            setModalType!('delete');
            openModal({ id, name });
          },
        },
      ];

      if (status === documentStatus.Draft || status === documentStatus.Active) {
        options.unshift({
          item: (
            <NavLink className={styles.navLink} to='/edit'>
              <Box display='flex' gap={1} justifyContent='start' alignItems='center'>
                <Edit color='primary' />
                Edit
              </Box>
            </NavLink>
          ),
          handler: () => {
            if (type) {
              navigate(`/documents/edit/${type.toLowerCase()}/${id}`);
            } else {
              navigate(`/documents/edit/document/${id}`);
            }
          },
        });
      } else {
        options.unshift({
          item: (
            <NavLink className={styles.navLink} to='/edit'>
              <Box display='flex' gap={1} justifyContent='start' alignItems='center'>
                <Visibility color='primary' />
                View
              </Box>
            </NavLink>
          ),
          handler: () => {
            if (type) {
              navigate(`/documents/view/${type.toLowerCase()}/${id}`);
            } else {
              navigate(`/documents/view/document/${id}`);
            }
          },
        });
      }

      return options;
    };

    return {
      name,
      status: formatTextUpperCase(status.replaceAll('_', ' ')),
      user: user?.name,
      recipients: getRecipients(templateJson),
      createdAt: dateToString(createdAt),
      // TODO: statusUpdates is currently coming as null from the back
      statusUpdated: dateToString(statusUpdated || updatedAt),
      more: <MenuButton tooltipLabel='More Actions' options={getRowOptions(status)} />,
    };
  });

  return (
    <Fragment>
      {isModalOpen && modalHandler()}
      <Box width='100%'>
        <DataTable
          columnData={[
            {
              id: 'name',
              name: 'File Name',
              align: 'left',
              enableSort: true,
            },
            { id: 'status', name: 'Status', align: 'left' },
            {
              id: 'user',
              name: 'Owner',
              align: 'left',
              enableSort: true,
            },
            {
              id: 'recipients',
              name: 'Recipients',
              align: 'left',
            },
            {
              id: 'createdAt',
              name: 'Created Date',
              align: 'left',
              enableSort: true,
            },
            {
              id: 'statusUpdated',
              name: 'Last Change',
              align: 'left',
              enableSort: true,
            },
            {
              id: 'more',
              name: 'More',
              align: 'left',
            },
          ]}
          rows={rowsData}
        />
      </Box>
    </Fragment>
  );
};

export default DocumentTable;
